export namespace TrackColor {
    export type RGB = `rgb(${number}, ${number}, ${number})`;
    export type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`;
    export type HEX = `#${string}`;

    export type Color = RGB | RGBA | HEX;
    export const DEFAULT_COLOR: Color = 'rgb(65, 95, 145)';

    export const DEFAULT_COLOR_TABLE_PARAMS: {
        minColor: Color,
        maxColors: Color[],
        steps: number,
        brightnessSteps: number
    } = {
        minColor: DEFAULT_COLOR,
        maxColors: ['#FF0000', '#FFFF00', '#FF7E00', '#00FF00', '#007EFF', '#00FFFF', '#0000FF'],
        steps: 6,
        brightnessSteps: 4,
    };

    export function generateColorTable(
        minColor: Color,
        maxColors: Color[],
        steps: number,
        brightnessSteps: number,
        greyTolerance: number = 0.2
    ): Color[] {
        const colors: Color[] = [];
        const minRGB = parseColor(minColor);

        maxColors.forEach(maxColor => {
            const maxRGB = parseColor(maxColor);

            for (let i = 0; i < steps; i++) {
                const ratio = i / (steps - 1);
                const r = Math.round(minRGB.r + (maxRGB.r - minRGB.r) * ratio);
                const g = Math.round(minRGB.g + (maxRGB.g - minRGB.g) * ratio);
                const b = Math.round(minRGB.b + (maxRGB.b - minRGB.b) * ratio);

                for (let j = 0; j < brightnessSteps; j++) {
                    const brightnessRatio = j / (brightnessSteps - 1);
                    const a = Math.round(255 * brightnessRatio);
                    const _r = (r + a)/2;
                    const _g = (g + a)/2;
                    const _b = (b + a)/2;
                    const componentAverage = (_r + _g + _b) / 3;
                    if (componentAverage < 25) continue; // Avoid too dark colors
                    const upperGrey = componentAverage * (1 + greyTolerance);
                    const lowerGrey = componentAverage * (1 - greyTolerance);
                    if (upperGrey > _r && upperGrey > _g && upperGrey > _b &&
                        lowerGrey < _r && lowerGrey < _g && lowerGrey < _b)
                        continue; // Avoid greyish colors
                    colors.push(`rgba(${(r + a) / 2}, ${(g + a) / 2}, ${(b + a) / 2})` as RGB);
                }
            }

        });
        return colors;
    }

    export function getRandomColor(): Color {
        const candidates = generateColorTable(DEFAULT_COLOR, DEFAULT_COLOR_TABLE_PARAMS.maxColors, DEFAULT_COLOR_TABLE_PARAMS.steps, DEFAULT_COLOR_TABLE_PARAMS.brightnessSteps);
        return candidates[Math.floor(Math.random() * candidates.length)];
    }

    function parseColor(color: string): { r: number, g: number, b: number } {
        color = color.trim();

        // Handle hex colors, e.g., "#abc" or "#aabbcc"
        if (color[0] === '#') {
            let hex = color.slice(1);
            if (hex.length === 3) {
                // Convert shorthand hex to full form, e.g. "abc" -> "aabbcc"
                hex = hex.split('').map(ch => ch + ch).join('');
            }
            if (hex.length !== 6) {
                throw new Error(`Invalid hex color: ${color}`);
            }
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return { r, g, b };
        }

        // Handle rgb() or rgba() colors
        const rgbRegex = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/;
        const match = color.match(rgbRegex);
        if (match) {
            const r = Number(match[1]);
            const g = Number(match[2]);
            const b = Number(match[3]);
            if ([r, g, b].some(c => c < 0 || c > 255)) {
                throw new Error(`Invalid RGB color components in: ${color}`);
            }
            return { r, g, b };
        }

        throw new Error(`Unsupported color format: ${color}`);
    }
}